import React from 'react';
import useInView from './useInView';
import { BENCHMARK_BASELINE, ENGAGEMENT, ENGAGEMENT_MULTIPLE, FEDERATION_ENGAGEMENT } from './siteFacts';

/**
 * Reproducible across federations — the most defensible proof we have.
 *
 * One bar per federation against the CHCS 1.5% Medicaid SMS line, which every
 * bar clears by 6–13×. Bars grow when scrolled into view; reduced motion shows
 * them at full length. Federations are labelled by program type, not by name.
 */
const MAX = 25;

const ReachBenchmark: React.FC<{ heading?: string }> = ({ heading = 'Reproducible across federations' }) => {
  const { ref, inView } = useInView<HTMLDivElement>();
  const baseline = (BENCHMARK_BASELINE / MAX) * 100;

  return (
    <section className="py-16 bg-white dark:bg-gray-950">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-ink">{heading}</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-2 max-w-2xl">{ENGAGEMENT.definition}</p>
          </div>
          <div className="text-left md:text-right">
            <p className="text-4xl font-bold text-gray-900 dark:text-gray-100">{ENGAGEMENT.value}</p>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {ENGAGEMENT_MULTIPLE.value} the {BENCHMARK_BASELINE}% CHCS baseline
            </p>
          </div>
        </div>
        <div ref={ref} className="relative">
          <ul className="space-y-4" aria-label="Engagement rate by federation">
            {FEDERATION_ENGAGEMENT.map((f, i) => (
              <li key={f.label} className="grid grid-cols-[minmax(120px,200px)_1fr_56px] items-center gap-3">
                <span className="text-sm text-gray-700 dark:text-gray-200">{f.label}</span>
                <span className="relative h-7 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                  <span
                    className="block h-full bg-v2accent rounded transition-[width] ease-out"
                    style={{ width: inView ? `${(f.rate / MAX) * 100}%` : '0%', transitionDuration: '900ms', transitionDelay: `${i * 150}ms` }}
                  />
                  <span className="absolute inset-y-0 border-l-2 border-dashed border-red-500" style={{ left: `${baseline}%` }} aria-hidden="true" />
                </span>
                <span className="text-right font-semibold tabular-nums text-gray-900 dark:text-gray-100">{f.rate.toFixed(1)}%</span>
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-red-600 dark:text-red-400">
            <span className="inline-block w-4 border-t-2 border-dashed border-red-500 align-middle mr-1" aria-hidden="true" />
            CHCS Medicaid SMS benchmark, {BENCHMARK_BASELINE}%
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReachBenchmark;
